import { useMemo, useState } from "react";
import { env } from "../config/env";
import { useI18n } from "../i18n/useI18n";
import type { UiKey } from "../i18n/ui";
import { pickInfrastructurePlan } from "../i18n/architecturePlanSimple";
import type { AwsRegionId } from "../lib/awsRegion";
import type { AudienceMode } from "../lib/audienceMode";
import type { DeploymentStackConfig } from "../lib/deploymentStack";
import type { DisplayCurrency } from "../lib/displayCurrency";
import { buildHtmlReport } from "../lib/exportHtmlReport";
import { downloadHtmlAsPdf } from "../lib/exportPdf";
import { buildMarkdownReport } from "../lib/exportReport";
import { normalizeComputeProfile } from "../lib/computeProfile";
import type { LlmPriceProfile } from "../lib/llmPriceProfile";
import type { NamedScenarioEntry } from "../lib/namedScenarios";
import type { PersistedUi } from "../lib/persistedUi";
import {
  buildAuditAppendixHtml,
  buildAuditAppendixMarkdown,
  buildReadonlyHtmlBanner,
  type ReportAuditContext,
} from "../lib/reportAppendix";
import {
  buildScenariosPackJson,
  buildScenariosPackMarkdown,
  packRowsFromNamedAndCurrent,
} from "../lib/scenariosPack";
import type { SavingsLevers } from "../lib/savingsLevers";
import type { SensitivityDeltas } from "../lib/sensitivity";
import type {
  CostEstimate,
  OptimizationResult,
  ScalingPoint,
  Workload,
} from "../lib/types";

type Props = {
  t: (key: UiKey) => string;
  region: AwsRegionId;
  workload: Workload;
  estimate: CostEstimate;
  optimization: OptimizationResult;
  scaling: ScalingPoint[];
  sensitivity: SensitivityDeltas;
  deploymentStack: DeploymentStackConfig;
  audienceMode: AudienceMode;
  savings: SavingsLevers;
  llmPriceProfile: LlmPriceProfile;
  budgetCapUsd: number | null;
  scenarioNotes: string;
  displayCurrency: DisplayCurrency;
  namedScenarios: NamedScenarioEntry[];
  currentSnapshot: PersistedUi;
};

export function ExportReportPanel({
  t,
  region,
  workload,
  estimate,
  optimization,
  scaling,
  sensitivity,
  deploymentStack,
  audienceMode,
  savings,
  llmPriceProfile,
  budgetCapUsd,
  scenarioNotes,
  displayCurrency,
  namedScenarios,
  currentSnapshot,
}: Props) {
  const { locale } = useI18n();
  const [copyState, setCopyState] = useState<"idle" | "ok" | "err">("idle");
  const [notionState, setNotionState] = useState<"idle" | "ok" | "err">("idle");
  const [pdfState, setPdfState] = useState<"idle" | "busy" | "err">("idle");

  const auditCtx: ReportAuditContext = useMemo(
    () => ({
      region,
      savings,
      deploymentStack,
      llmProfile: llmPriceProfile,
      computeProfile: normalizeComputeProfile(currentSnapshot.computeProfile),
      audienceMode,
      budgetCapUsd,
      scenarioNotes: scenarioNotes.trim() || undefined,
      displayCurrency,
    }),
    [
      region,
      savings,
      deploymentStack,
      llmPriceProfile,
      currentSnapshot.computeProfile,
      audienceMode,
      budgetCapUsd,
      scenarioNotes,
      displayCurrency,
    ],
  );

  const appendixMd = useMemo(
    () => buildAuditAppendixMarkdown(locale, t, auditCtx),
    [locale, t, auditCtx],
  );

  const appendixHtml = useMemo(
    () => buildAuditAppendixHtml(locale, t, auditCtx),
    [locale, t, auditCtx],
  );

  const md = useMemo(
    () =>
      buildMarkdownReport({
        locale,
        region,
        workload,
        estimate,
        optimization,
        title: env.appName,
        deploymentStack,
        audience: audienceMode,
        appendixMd,
      }),
    [
      locale,
      region,
      workload,
      estimate,
      optimization,
      deploymentStack,
      audienceMode,
      appendixMd,
    ],
  );

  const reportHtmlOpts = useMemo(
    () => ({
      locale,
      region,
      workload,
      estimate,
      optimization,
      title: env.appName,
      scaling,
      sensitivity,
      plan: pickInfrastructurePlan(locale, workload, deploymentStack, audienceMode),
      labels: {
        mix: t("mix.title"),
        scaling: t("scaling.title"),
        sensitivity: t("sensitivity.title"),
        breakdown: t("breakdown.title"),
        workload: t("export.workloadSection"),
        assumptions: t("export.assumptionsHeading"),
        risks: t("rec.risks"),
        suggestions: t("rec.suggestions"),
        architecture: t("rec.arch"),
        footer: t("footer.note"),
        printHint: t("export.printHint"),
        scalingCaption: t("scaling.sub"),
        sensitivityDau: t("sensitivity.dau"),
        sensitivityReq: t("sensitivity.req"),
        sensitivityAiShare: t("sensitivity.aiShare"),
        sensitivityTokens: t("sensitivity.tokens"),
        total: t("breakdown.total"),
        mixColItem: t("export.colItem"),
        mixColPct: t("export.colPct"),
        mixColUsd: t("export.colUsd"),
        planBlueprint: t("plan.title"),
        contactLead: t("footer.contactLead"),
        legalHeading: t("export.legalHeading"),
        legalBody: t("footer.legal"),
      },
      contactEmail: env.contactEmail,
      appendixHtml,
    }),
    [
      locale,
      region,
      workload,
      estimate,
      optimization,
      scaling,
      sensitivity,
      deploymentStack,
      audienceMode,
      t,
      appendixHtml,
    ],
  );

  function buildReportHtml(): string {
    return buildHtmlReport(reportHtmlOpts);
  }

  function buildReadonlyHtml(): string {
    return buildHtmlReport({
      ...reportHtmlOpts,
      bodyTopHtml: buildReadonlyHtmlBanner(t),
    });
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(md);
      setCopyState("ok");
      setTimeout(() => setCopyState("idle"), 2000);
    } catch {
      setCopyState("err");
      setTimeout(() => setCopyState("idle"), 2500);
    }
  }

  async function copyNotion() {
    try {
      await navigator.clipboard.writeText(md);
      setNotionState("ok");
      setTimeout(() => setNotionState("idle"), 2000);
    } catch {
      setNotionState("err");
      setTimeout(() => setNotionState("idle"), 2500);
    }
  }

  function downloadMd() {
    const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "cost-estimate.md";
    a.click();
    URL.revokeObjectURL(url);
  }

  function downloadHtml() {
    const html = buildReportHtml();
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "cost-estimate-report.html";
    a.click();
    URL.revokeObjectURL(url);
  }

  function downloadReadonlyHtml() {
    const html = buildReadonlyHtml();
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "cost-estimate-readonly.html";
    a.click();
    URL.revokeObjectURL(url);
  }

  function downloadPackMd() {
    const rows = packRowsFromNamedAndCurrent(
      namedScenarios,
      t("namedScenarios.currentLabel"),
      currentSnapshot,
    );
    const text = buildScenariosPackMarkdown(locale, t, rows);
    const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "scenarios-pack.md";
    a.click();
    URL.revokeObjectURL(url);
  }

  function downloadPackJson() {
    const rows = packRowsFromNamedAndCurrent(
      namedScenarios,
      t("namedScenarios.currentLabel"),
      currentSnapshot,
    );
    const text = buildScenariosPackJson(locale, rows);
    const blob = new Blob([text], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "scenarios-pack.json";
    a.click();
    URL.revokeObjectURL(url);
  }

  async function downloadPdf() {
    setPdfState("busy");
    try {
      await downloadHtmlAsPdf(buildReportHtml(), "cost-estimate-report.pdf");
      setPdfState("idle");
    } catch {
      setPdfState("err");
    }
  }

  return (
    <div className="export-panel">
      <h3 className="title">{t("toolbar.exportTitle")}</h3>
      <p className="pack-hint">{t("namedScenarios.packHint")}</p>
      <div className="actions">
        <button type="button" className="btn" onClick={() => void copy()}>
          {t("export.copy")}
        </button>
        <button
          type="button"
          className="btn secondary"
          onClick={() => void copyNotion()}
        >
          {t("export.copyNotion")}
        </button>
        <button type="button" className="btn secondary" onClick={downloadMd}>
          {t("export.download")}
        </button>
        <button type="button" className="btn html" onClick={downloadHtml}>
          {t("export.downloadHtml")}
        </button>
        <button type="button" className="btn secondary" onClick={downloadReadonlyHtml}>
          {t("export.downloadReadonlyHtml")}
        </button>
        <button type="button" className="btn secondary" onClick={downloadPackMd}>
          {t("export.downloadPackMd")}
        </button>
        <button type="button" className="btn secondary" onClick={downloadPackJson}>
          {t("export.downloadPackJson")}
        </button>
        <button
          type="button"
          className="btn pdf"
          disabled={pdfState === "busy"}
          onClick={() => void downloadPdf()}
        >
          {pdfState === "busy" ? t("export.pdfBusy") : t("export.downloadPdf")}
        </button>
      </div>
      {copyState === "ok" && <p className="msg ok">{t("export.copied")}</p>}
      {copyState === "err" && <p className="msg err">{t("export.failed")}</p>}
      {notionState === "ok" && <p className="msg ok">{t("export.notionCopied")}</p>}
      {notionState === "err" && <p className="msg err">{t("export.failed")}</p>}
      {pdfState === "err" && <p className="msg err">{t("export.pdfError")}</p>}

      <style>{`
        .export-panel {
          border-left: 1px solid var(--border);
          padding-left: 1.25rem;
          min-width: 0;
        }
        @media (max-width: 900px) {
          .export-panel {
            border-left: none;
            padding-left: 0;
            padding-top: 1rem;
            border-top: 1px solid var(--border);
          }
        }
        .title {
          font-size: 0.8rem;
          font-weight: 600;
          margin: 0 0 0.35rem;
          color: var(--muted);
          line-height: 1.3;
          min-height: 2.6em;
          display: flex;
          align-items: flex-end;
        }
        .pack-hint {
          margin: 0 0 0.5rem;
          font-size: 0.72rem;
          line-height: 1.4;
          color: var(--muted);
          max-width: 28rem;
        }
        .actions {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          align-items: center;
        }
        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1px solid var(--accent-dim);
          background: var(--accent-chip);
          color: var(--accent);
          padding: 0.45rem 0.75rem;
          min-height: 2.375rem;
          box-sizing: border-box;
          border-radius: 8px;
          cursor: pointer;
          font-size: 0.85rem;
          font-family: var(--font);
          font-weight: 600;
        }
        .btn:disabled {
          opacity: 0.55;
          cursor: wait;
        }
        .btn.secondary {
          border-color: var(--border);
          background: var(--surface2);
          color: var(--text);
        }
        .btn.html {
          border-color: #4a6a8a;
          background: var(--accent-chip-pdf);
          color: #9ec8e8;
        }
        .btn.pdf {
          border-color: #8b4040;
          background: var(--accent-chip-danger);
          color: #f0a8a8;
        }
        .msg {
          margin: 0.5rem 0 0;
          font-size: 0.8rem;
        }
        .msg.ok {
          color: var(--accent);
        }
        .msg.err {
          color: #e88;
        }
        @media (max-width: 640px) {
          .actions {
            flex-direction: column;
          }
          .btn {
            width: 100%;
            min-height: 48px;
            font-size: 0.9rem;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}
